(function () {
  'use strict';

  if (!window.__CART_RESTRICTIONS_ENABLED__) {
    return;
  }

  // Read cart restriction rules from an element's data-rules-* attributes
  function parseRulesFromElement(el) {
    if (!el) {
      return { productType: '', maxPerCart: null, incompatibleTypes: [] };
    }

    const productType = el.getAttribute('data-rules-product-type') || '';
    const maxPerCartStr = el.getAttribute('data-rules-max-per-cart') || '';
    const incompatibleTypesStr = el.getAttribute('data-rules-incompatible-types') || '[]';

    let maxPerCart = null;
    if (maxPerCartStr && maxPerCartStr !== '') {
      const parsed = parseInt(maxPerCartStr, 10);
      if (!isNaN(parsed)) {
        maxPerCart = parsed;
      }
    }

    let incompatibleTypes = [];
    try {
      const parsed = JSON.parse(incompatibleTypesStr);
      if (Array.isArray(parsed)) {
        incompatibleTypes = parsed;
      } else if (typeof parsed === 'string' && parsed !== '') {
        incompatibleTypes = [parsed];
      } else if (parsed && typeof parsed === 'object') {
        incompatibleTypes = [];
      }
    } catch (e) {
      if (incompatibleTypesStr && incompatibleTypesStr !== '[]' && incompatibleTypesStr !== '') {
        incompatibleTypes = [incompatibleTypesStr];
      } else {
        incompatibleTypes = [];
      }
    }

    return { productType, maxPerCart, incompatibleTypes };
  }

  // Normalize incompatibleTypes to a flat array regardless of input
  function normalizeIncompatibleTypes(value) {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === 'string') {
      if (value === '' || value === '[]') {
        return [];
      }
      try {
        const parsed = JSON.parse(value);
        if (Array.isArray(parsed)) {
          return parsed;
        }
        if (typeof parsed === 'string' && parsed !== '') {
          return [parsed];
        }
      } catch (e) {
        return value ? [value] : [];
      }
      return value ? [value] : [];
    }
    return [];
  }

  // Build cart state from /cart.js API (used on product pages)
  async function getCartStateFromAjax() {
    try {
      const map = window.__CART_ITEMS_BY_VARIANT_ID__;
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const qtyByProductType = {};
      const productTypesPresent = [];
      const cartIncompatibleMap = {};

      for (const item of cart.items) {
        const cartItemData = map && map[item.variant_id];
        if (cartItemData && cartItemData.productType) {
          let productType = cartItemData.productType;
          if (Array.isArray(productType)) {
            productType = productType[0] || '';
          }
          qtyByProductType[productType] = (qtyByProductType[productType] || 0) + item.quantity;
          if (!productTypesPresent.includes(productType)) {
            productTypesPresent.push(productType);
          }
          const itemIncompat = normalizeIncompatibleTypes(cartItemData.incompatibleTypes);
          for (let i = 0; i < itemIncompat.length; i++) {
            if (!cartIncompatibleMap[itemIncompat[i]]) {
              cartIncompatibleMap[itemIncompat[i]] = productType;
            }
          }
        }
      }

      return { qtyByProductType, productTypesPresent, cartIncompatibleMap };
    } catch (error) {
      console.error('[Cart Restrictions] getCartStateFromAjax: Error fetching cart state', error);
      return { qtyByProductType: {}, productTypesPresent: [], cartIncompatibleMap: {} };
    }
  }

  // Build cart state from .cart-item DOM elements (used on cart page)
  function getCartStateFromDOM() {
    const cartItems = document.querySelectorAll('.cart-item');
    const qtyByProductType = {};
    const productTypesPresent = [];
    const cartIncompatibleMap = {};

    cartItems.forEach((item) => {
      const quantityInput = item.querySelector('input[data-quantity-sku], input[name="updates[]"]');
      const quantity = quantityInput ? parseInt(quantityInput.value) || 0 : 0;

      const rules = parseRulesFromElement(item);
      if (rules.productType) {
        const productType = rules.productType;
        qtyByProductType[productType] = (qtyByProductType[productType] || 0) + quantity;
        if (!productTypesPresent.includes(productType)) {
          productTypesPresent.push(productType);
        }
        const itemIncompat = normalizeIncompatibleTypes(rules.incompatibleTypes);
        for (let i = 0; i < itemIncompat.length; i++) {
          if (!cartIncompatibleMap[itemIncompat[i]]) {
            cartIncompatibleMap[itemIncompat[i]] = productType;
          }
        }
      }
    });

    return { qtyByProductType, productTypesPresent, cartIncompatibleMap };
  }

  // Fallback English messages
  const FALLBACK_MESSAGES = {
    maxPerCartOne: 'Only [max_count] [product_type] product can be added to the cart.',
    maxPerCartOther: 'Only [max_count] [product_type] products can be added to the cart.',
    incompatibleTypes: '[incoming_type] and [incompatible_type] products cannot be combined in the same cart.'
  };

  function buildRestrictionMessage(templateKey, replacements) {
    const templates = window.__CART_RESTRICTION_MESSAGES__ || {};
    let template = templates[templateKey] || FALLBACK_MESSAGES[templateKey] || '';
    for (const key in replacements) {
      template = template.split('[' + key + ']').join(replacements[key]);
    }
    return template;
  }

  // Validate an incoming product against the current cart state
  function validateIncoming(existingState, incomingRules, qtyDelta) {
    if (!incomingRules.productType || incomingRules.productType === '') {
      return { allowed: true };
    }

    const normalizedIncompatibleTypes = normalizeIncompatibleTypes(incomingRules.incompatibleTypes);
    const currentQty = existingState.qtyByProductType[incomingRules.productType] || 0;
    const newQty = currentQty + qtyDelta;

    if (incomingRules.maxPerCart !== null && newQty > incomingRules.maxPerCart) {
      const productTypeName = incomingRules.productType.charAt(0).toUpperCase() + incomingRules.productType.slice(1);
      const templateKey = incomingRules.maxPerCart === 1 ? 'maxPerCartOne' : 'maxPerCartOther';
      const reason = buildRestrictionMessage(templateKey, {
        max_count: incomingRules.maxPerCart,
        product_type: productTypeName
      });
      return { allowed: false, reason };
    }

    if (normalizedIncompatibleTypes && normalizedIncompatibleTypes.length > 0) {
      for (const incompatibleType of normalizedIncompatibleTypes) {
        if (existingState.productTypesPresent.includes(incompatibleType)) {
          const incomingProductTypeName = incomingRules.productType.charAt(0).toUpperCase() + incomingRules.productType.slice(1);
          const incompatibleTypeName = incompatibleType.charAt(0).toUpperCase() + incompatibleType.slice(1);
          const reason = buildRestrictionMessage('incompatibleTypes', {
            incoming_type: incomingProductTypeName,
            incompatible_type: incompatibleTypeName
          });
          return { allowed: false, reason };
        }
      }
    }

    const cartIncompatMap = existingState.cartIncompatibleMap || {};
    if (cartIncompatMap[incomingRules.productType]) {
      const declaringType = cartIncompatMap[incomingRules.productType];
      const incomingProductTypeName = incomingRules.productType.charAt(0).toUpperCase() + incomingRules.productType.slice(1);
      const declaringTypeName = declaringType.charAt(0).toUpperCase() + declaringType.slice(1);
      const reason = buildRestrictionMessage('incompatibleTypes', {
        incoming_type: incomingProductTypeName,
        incompatible_type: declaringTypeName
      });
      return { allowed: false, reason };
    }

    return { allowed: true };
  }

  // Re-validate the entire cart at checkout time
  function validateCartForCheckout(cartState) {
    const cartItems = document.querySelectorAll('.cart-item');

    for (const item of cartItems) {
      const rules = parseRulesFromElement(item);
      if (!rules.productType || rules.productType === '') {
        continue;
      }

      const quantityInput = item.querySelector('input[data-quantity-sku], input[name="updates[]"]');
      const quantity = quantityInput ? parseInt(quantityInput.value) || 0 : 0;

      if (rules.maxPerCart !== null && quantity > rules.maxPerCart) {
        const productTypeName = rules.productType.charAt(0).toUpperCase() + rules.productType.slice(1);
        const templateKey = rules.maxPerCart === 1 ? 'maxPerCartOne' : 'maxPerCartOther';
        const reason = buildRestrictionMessage(templateKey, {
          max_count: rules.maxPerCart,
          product_type: productTypeName
        });
        return { allowed: false, reason };
      }

      if (rules.incompatibleTypes && rules.incompatibleTypes.length > 0) {
        for (const incompatibleType of rules.incompatibleTypes) {
          if (cartState.productTypesPresent.includes(incompatibleType)) {
            const productTypeName = rules.productType.charAt(0).toUpperCase() + rules.productType.slice(1);
            const incompatibleTypeName = incompatibleType.charAt(0).toUpperCase() + incompatibleType.slice(1);
            const reason = buildRestrictionMessage('incompatibleTypes', {
              incoming_type: productTypeName,
              incompatible_type: incompatibleTypeName
            });
            return { allowed: false, reason };
          }
        }
      }
    }

    return { allowed: true };
  }

  // Show the restriction modal, falling back to alert()
  function showIncompatibilityModal(reason) {
    try {
      const cartRestrictionModal = FlowbiteInstances.getInstance('Modal', 'cart-restriction-modal');
      document.getElementById('cart-restriction-message').textContent = reason;
      cartRestrictionModal.show();
    } catch (e) {
      console.error('[Cart Restrictions] showIncompatibilityModal: Modal error, falling back to alert', e);
      alert(reason);
    }
  }

  // Apply rules data attributes to a button from __PRODUCT_RULES_BY_ID__ map
  function applyRulesToButton(button, rules) {
    if (rules) {
      let productType = rules.productType || '';
      if (Array.isArray(productType)) {
        productType = productType[0] || '';
      }
      let maxPerCart = rules.maxPerCart;
      if (Array.isArray(maxPerCart)) {
        maxPerCart = maxPerCart[0] !== undefined ? maxPerCart[0] : null;
      }
      button.setAttribute('data-rules-product-type', productType);
      button.setAttribute('data-rules-max-per-cart', maxPerCart !== null ? maxPerCart : '');
      const incompatibleTypes = normalizeIncompatibleTypes(rules.incompatibleTypes);
      button.setAttribute('data-rules-incompatible-types', JSON.stringify(incompatibleTypes));
    } else {
      button.setAttribute('data-rules-product-type', '');
      button.setAttribute('data-rules-max-per-cart', '');
      button.setAttribute('data-rules-incompatible-types', '[]');
    }
  }

  // Sync button rules from a variant ID (looks up __PRODUCT_RULES_BY_ID__). Exposed globally so variant-change handlers in sections can call it.
  window.updateButtonRulesFromVariantId = function (button, variantId) {
    if (!button || !variantId) {
      return;
    }

    const map = window.__PRODUCT_RULES_BY_ID__;
    if (!map) {
      return;
    }

    const variantIdStr = variantId.toString();
    applyRulesToButton(button, map[variantIdStr] || null);
  };

  // Sync button rules from a product object with a .rules property. Used by fizz-quiz-2-0 and any section that builds product data in JS.
  window.updateButtonRulesFromProduct = function (button, product) {
    if (!button || !product || !product.rules) {
      if (button) {
        applyRulesToButton(button, null);
      }
      return;
    }
    applyRulesToButton(button, product.rules);
  };

  // Intercept add-to-cart: validate before calling the original handler
  async function validateAndAddToCart(event) {
    let button = event.target;

    if (!button.hasAttribute('data-variant-id')) {
      button = button.closest('.add-to-cart-btn');
    }
    if (!button) {
      return;
    }

    const incomingRules = parseRulesFromElement(button);
    if (!incomingRules.productType || incomingRules.productType === '') {
      return;
    }

    if (incomingRules.maxPerCart === null && incomingRules.incompatibleTypes.length === 0) {
      return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    let quantity = parseInt(button.getAttribute('data-quantity')) || 1;

    try {
      const existingState = await getCartStateFromAjax();
      const validationResult = validateIncoming(existingState, incomingRules, quantity);

      if (validationResult.allowed) {
        if (typeof addToCartFromButton === 'function') {
          addToCartFromButton(button);
        }
      } else {
        showIncompatibilityModal(validationResult.reason);
      }
    } catch (error) {
      console.error('[Cart Restrictions] validateAndAddToCart error:', error);
      if (typeof addToCartFromButton === 'function') {
        addToCartFromButton(button);
      }
    }
  }

  // Wire up all event listeners on DOMContentLoaded
  document.addEventListener('DOMContentLoaded', function () {

    // Intercept add-to-cart button clicks (capture phase runs before global.js)
    document.querySelectorAll('.add-to-cart-btn').forEach(function (button) {
      button.addEventListener('click', validateAndAddToCart, true);
    });

    // Intercept plus-button clicks on the cart page
    document.querySelectorAll('.quantity__button[name="plus"]').forEach(function (button) {
      button.addEventListener('click', async function (event) {
        let quantityInput = this.parentElement.querySelector('input[data-quantity-sku]');
        if (!quantityInput) {
          quantityInput = this.closest('.quantity').querySelector('input[data-quantity-sku]') ||
            this.closest('form').querySelector('input[data-quantity-sku]') ||
            document.querySelector('input[data-quantity-sku]');
        }

        if (!quantityInput) {
          return;
        }

        const cartItem = quantityInput.closest('.cart-item');
        if (!cartItem) {
          return;
        }

        const rules = parseRulesFromElement(cartItem);
        if (!rules.productType || rules.productType === '') {
          return;
        }

        if (rules.maxPerCart === null && rules.incompatibleTypes.length === 0) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();

        try {
          const existingState = await getCartStateFromAjax();
          const validationResult = validateIncoming(existingState, rules, 1);

          if (!validationResult.allowed) {
            showIncompatibilityModal(validationResult.reason);
          } else {
            const currentValue = parseInt(quantityInput.value) || 0;
            quantityInput.value = currentValue + 1;

            const changeEvent = new Event('change', { bubbles: true });
            quantityInput.dispatchEvent(changeEvent);
          }
        } catch (error) {
          const currentValue = parseInt(quantityInput.value) || 0;
          quantityInput.value = currentValue + 1;
          const changeEvent = new Event('change', { bubbles: true });
          quantityInput.dispatchEvent(changeEvent);
        }
      }, true);
    });

    // Intercept quantity input changes on the cart page
    document.querySelectorAll('input[data-quantity-sku]').forEach(function (input) {
      let originalValue = parseInt(input.value) || 0;
      input.dataset.lastValidValue = originalValue;

      const validateQuantityChange = async function (event) {
        const cartItem = input.closest('.cart-item');
        if (!cartItem) {
          return;
        }

        const rules = parseRulesFromElement(cartItem);
        if (!rules.productType || rules.productType === '') {
          return;
        }

        if (rules.maxPerCart === null && rules.incompatibleTypes.length === 0) {
          return;
        }

        const newValue = parseInt(input.value) || 0;
        const lastValidValue = parseInt(input.dataset.lastValidValue) || 0;

        if (newValue > lastValidValue) {
          input.value = lastValidValue;

          try {
            const existingState = await getCartStateFromAjax();
            const qtyDelta = newValue - lastValidValue;
            const validationResult = validateIncoming(existingState, rules, qtyDelta);

            if (!validationResult.allowed) {
              setTimeout(function () {
                showIncompatibilityModal(validationResult.reason);
              }, 50);
            } else {
              input.value = newValue;
              input.dataset.lastValidValue = newValue;

              const changeEvent = new Event('change', { bubbles: true });
              input.dispatchEvent(changeEvent);
            }
          } catch (error) {
            console.error('[Cart Restrictions] quantity input change: Error fetching cart', error);
            input.value = newValue;
            input.dataset.lastValidValue = newValue;
          }
        } else if (newValue <= lastValidValue && newValue >= 1) {
          input.dataset.lastValidValue = newValue;
        } else if (newValue < 1) {
          input.value = lastValidValue;
        }
      };

      input.addEventListener('input', validateQuantityChange);
      input.addEventListener('change', validateQuantityChange);

      input.addEventListener('click', async function (event) {
        const cartItem = input.closest('.cart-item');
        if (!cartItem) {
          return;
        }

        const rules = parseRulesFromElement(cartItem);
        if (!rules.productType || rules.productType === '') {
          return;
        }

        if (rules.maxPerCart === null && rules.incompatibleTypes.length === 0) {
          return;
        }

        const currentValue = parseInt(input.value) || 0;
        const maxValue = parseInt(input.max) || Infinity;

        if (currentValue >= maxValue) {
          try {
            const existingState = await getCartStateFromAjax();
            const validationResult = validateIncoming(existingState, rules, 1);

            if (!validationResult.allowed) {
              setTimeout(function () {
                showIncompatibilityModal(validationResult.reason);
              }, 100);
            }
          } catch (error) {
            console.error('[Cart Restrictions] quantity input click: Error fetching cart', error);
          }
        }
      });
    });

    // Intercept checkout button click
    const checkoutButton = document.querySelector('#checkout');
    if (checkoutButton && checkoutButton.form) {
      checkoutButton.addEventListener('click', function (event) {
        try {
          const cartState = getCartStateFromDOM();
          const validationResult = validateCartForCheckout(cartState);
          if (!validationResult.allowed) {
            event.preventDefault();
            showIncompatibilityModal(validationResult.reason);
          }
        } catch (error) {
          console.error('[Cart Restrictions] Checkout validation error:', error);
        }
      });
    }

  });
})();
